/**
 * Questionnaire follow-up label — Feedback #841 (Shawn, 2026-10-19).
 *
 * "Send the questionnaire to the prospective client at the end of each call;
 * if they don't send it back, we don't need to bother reviewing."
 *
 * The API decides the state (api/src/lib/questionnaire.js
 * questionnaireFollowup, one rule for the lead list, the drawer, the
 * phone-intake queue, the Dashboard card and the reviewer digest) and sends it
 * as `questionnaire_followup`:
 *   { state: 'waiting', until }          → "Questionnaire sent — waiting until <date>"
 *   { state: 'not_returned', expired_at } → "Questionnaire not returned — send non-engagement letter"
 *   null                                  → nothing to chase (submitted, voided, none,
 *                                            already decided or converted)
 *
 * This module only words it.  Keep the wording equal to the server twin
 * (followupLabelText) so the digest email and the screen say the same thing.
 * It is a label: no surface hides the lead or declines it automatically; the
 * Decline flow in the drawer stays a human action.
 */
import { firmTz } from '../utils/eventTime'

export const FOLLOWUP_WAITING = 'waiting'
export const FOLLOWUP_NOT_RETURNED = 'not_returned'
export const NOT_RETURNED_LABEL = 'Questionnaire not returned — send non-engagement letter'

/** The expiry day in the FIRM's zone (never the browser's), e.g. "Nov 2, 2026". */
export function followupDay (iso, tz = firmTz()) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }).format(d)
  } catch {
    return ''
  }
}

/** Full label text, or '' when there is nothing to show. */
export function followupLabel (f, tz) {
  if (!f || typeof f !== 'object') return ''
  if (f.state === FOLLOWUP_NOT_RETURNED) return NOT_RETURNED_LABEL
  if (f.state === FOLLOWUP_WAITING) {
    const day = followupDay(f.until, tz)
    return day ? `Questionnaire sent — waiting until ${day}` : 'Questionnaire sent — waiting for the client'
  }
  return ''
}

/** Chip colours: amber = act now (send the letter), blue = waiting. */
export function followupChipClass (f) {
  if (f?.state === FOLLOWUP_NOT_RETURNED) return 'bg-amber-100 text-amber-900 border border-amber-300'
  if (f?.state === FOLLOWUP_WAITING) return 'bg-blue-50 text-blue-800 border border-blue-200'
  return ''
}

export function followupIcon (f) {
  if (f?.state === FOLLOWUP_NOT_RETURNED) return '📭'
  if (f?.state === FOLLOWUP_WAITING) return '⏳'
  return ''
}
